import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createNote, updateNote, getAllNotes, getNoteById, emailNote } from "./NoteService";
import { apiFetch } from "../api";
import { BASE_URL as base } from "../../commonUrls";
import NoteEditor from "./NoteEditor";
import { buildNoteIndex, extractNoteLinks, findBacklinks, resolveLinkedNotes } from "./NoteLinkUtils";
import "./NoteForm.css";

export default function NoteForm() {
  const nav = useNavigate();
  const { state } = useLocation();   // edit mode
  const editNote = state?.note;
  const readOnly = Boolean(state?.readOnly);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [noteType, setNoteType] = useState("INFO");
  const [priority, setPriority] = useState(4);
  const [status, setStatus] = useState("ACTIVE");
  const [secured, setSecured] = useState(false);
  const [noteSummary, setNoteSummary] = useState("");
  const [linkedNotes, setLinkedNotes] = useState([]);
  const [backlinks, setBacklinks] = useState([]);
  const [linkMetaLoading, setLinkMetaLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [pinned, setPinned] = useState(false);
  const [favorite, setFavorite] = useState(false);
  const [deleteProtected, setDeleteProtected] = useState(false);
  const [reminderAt, setReminderAt] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [existingFiles, setExistingFiles] = useState([]);
  const [fileSearch, setFileSearch] = useState("");
  const [selectedExistingIds, setSelectedExistingIds] = useState(new Set());

  const FILES_URL = `${base}/files`;

  const toInputDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const tzOffset = date.getTimezoneOffset() * 60000;
    const local = new Date(date.getTime() - tzOffset);
    return local.toISOString().slice(0, 16);
  };

  const applyNoteData = (note) => {
    if (!note) return;
    setTitle(note.title || "");
    setContent(note.content || "");
    setNoteType(note.noteType || "INFO");
    setPriority(note.priority ?? 4);
    setStatus(note.status || "ACTIVE");
    setSecured(Boolean(note.secured));
    setNoteSummary(note.noteSummary || "");
    setTags(Array.isArray(note.tags) ? note.tags : []);
    setPinned(Boolean(note.pinned));
    setFavorite(Boolean(note.favorite));
    setDeleteProtected(Boolean(note.deleteProtected));
    setReminderAt(toInputDateTime(note.reminderAt));
    setAttachments(Array.isArray(note.attachments) ? note.attachments : []);
    const ids = new Set((note.attachments || []).map((file) => file.id).filter(Boolean));
    setSelectedExistingIds(ids);
  };

  useEffect(() => {
    if (!editNote?.id) return;
    applyNoteData(editNote);
    let active = true;
    getNoteById(editNote.id)
      .then((note) => {
        if (!active || !note) return;
        applyNoteData(note);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [editNote?.id]);

  useEffect(() => {
    if (!editNote?.id) return;
    let active = true;
    setLinkMetaLoading(true);
    getAllNotes()
      .then((notes) => {
        if (!active) return;
        const allNotes = Array.isArray(notes) ? notes : [];
        const index = buildNoteIndex(allNotes.filter((n) => n.id !== editNote.id));
        const links = extractNoteLinks(editNote.content);
        setLinkedNotes(resolveLinkedNotes(links, index));
        setBacklinks(findBacklinks(allNotes.filter((n) => n.id !== editNote.id), editNote.title));
      })
      .catch(() => {
        if (!active) return;
        setLinkedNotes([]);
        setBacklinks([]);
      })
      .finally(() => {
        if (!active) return;
        setLinkMetaLoading(false);
      });
    return () => {
      active = false;
    };
  }, [editNote?.id, editNote?.content, editNote?.title]);

  useEffect(() => {
    let active = true;
    apiFetch(`${FILES_URL}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!active) return;
        const list = Array.isArray(data) ? data : [];
        setExistingFiles(list);
      })
      .catch(() => {
        if (!active) return;
        setExistingFiles([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (readOnly) return;

    const uploaded = await uploadAttachments();
    const attachmentList = [...attachments, ...uploaded].filter(Boolean);
    const linkedIds = Array.from(selectedExistingIds);

    const payload = {
      title,
      content,
      noteType,
      priority,
      status,
      secured,
      noteSummary,
      tags,
      pinned,
      favorite,
      deleteProtected,
      reminderAt: reminderAt || null,
      attachmentIds: [...new Set([
        ...attachmentList.map((file) => file.id).filter(Boolean),
        ...linkedIds,
      ])],
    };

    if (editNote) {
      await updateNote(editNote.id, payload);
    } else {
      const created = await createNote(payload);
      if (created?.id && payload.attachmentIds.length > 0) {
        await updateNote(created.id, payload);
      }
    }

    nav("/notes");
  };

  const uploadAttachments = async (filesOverride) => {
    const fallback = Array.from(fileInputRef.current?.files || []);
    const pending = filesOverride?.length ? filesOverride : selectedFiles;
    const files = pending.length ? pending : fallback;
    if (files.length === 0) return [];
    const fd = new FormData();
    files.forEach((file) => fd.append("files", file, file.name || "attachment"));
    try {
      setUploading(true);
      const res = await apiFetch(`${FILES_URL}/upload`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        let detail = "";
        try {
          const text = await res.text();
          if (text) detail = `\n${text}`;
        } catch (_e) {}
        alert(`Upload failed (${res.status || "network"}). Please try again.${detail}`);
        return [];
      }
      const uploaded = await res.json();
      const next = Array.isArray(uploaded) ? uploaded : [];
      setAttachments((prev) => [...prev, ...next]);
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return next;
    } catch (_e) {
      alert("Upload failed (network). Please try again.");
      return [];
    } finally {
      setUploading(false);
    }
  };

  const fetchAttachmentBlob = async (file) => {
    if (!file?.id) return null;
    const res = await apiFetch(`${FILES_URL}/${file.id}/download`);
    if (!res.ok) return null;
    return res.blob();
  };

  const openAttachment = async (file) => {
    try {
      const blob = await fetchAttachmentBlob(file);
      if (!blob) {
        alert("Unable to open file.");
        return;
      }
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (_e) {
      alert("Unable to open file.");
    }
  };

  const downloadAttachment = async (file) => {
    try {
      const blob = await fetchAttachmentBlob(file);
      if (!blob) {
        alert("Download failed.");
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.fileName || "attachment";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) {
      alert("Download failed.");
    }
  };

  const handleEmailNote = async () => {
    if (!editNote?.id) return;
    try {
      const res = await emailNote(editNote.id);
      if (!res.ok) {
        let detail = "";
        try {
          const text = await res.text();
          if (text) detail = `\n${text}`;
        } catch (_e) {}
        alert(`Email failed (${res.status || "network"}).${detail}`);
        return;
      }
      alert("Note emailed successfully.");
    } catch (_e) {
      alert("Email failed. Please try again.");
    }
  };

  const toggleExistingFile = (fileId) => {
    setSelectedExistingIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const filteredExistingFiles = existingFiles.filter((file) => {
    if (!fileSearch.trim()) return true;
    return (file.fileName || "").toLowerCase().includes(fileSearch.trim().toLowerCase());
  });

  return (
    <form className="note-form-wrap" onSubmit={submit}>
      <h2>{readOnly ? "View Note" : editNote ? "Edit Note" : "New Note"}</h2>
      <div className="note-form-subtitle">
        Write with rich formatting, then organize with tags and links.
      </div>

      <div className="note-form-grid">
        <section className="note-form-main">
          <label>Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} disabled={readOnly} />

          <label>Content</label>
          <NoteEditor
            value={content}
            onChange={setContent}
            placeholder="Write your note with rich formatting, tables, and links."
            readOnly={readOnly}
          />
        </section>

        <aside className="note-form-meta">
          <div className="note-form-card">
            <label>Pin + Favorite</label>
            <div className="note-form-toggle-row">
              <button
                type="button"
                className={`note-form-pill ${pinned ? "active" : ""}`}
                onClick={() => setPinned((prev) => !prev)}
                disabled={readOnly}
              >
                📌 Pinned
              </button>
              <button
                type="button"
                className={`note-form-pill ${favorite ? "active" : ""}`}
                onClick={() => setFavorite((prev) => !prev)}
                disabled={readOnly}
              >
                ⭐ Favorite
              </button>
            </div>
          </div>

          <div className="note-form-card">
            <label>Reminder</label>
            <input
              type="datetime-local"
              value={reminderAt}
              onChange={(e) => setReminderAt(e.target.value)}
              disabled={readOnly}
            />
          </div>

          <div className="note-form-card">
            <label>Attachments</label>
            <div className="note-form-attachment-input">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
                disabled={readOnly}
              />
              <button type="button" onClick={uploadAttachments} disabled={uploading || readOnly}>
                {uploading ? "Uploading..." : "Upload"}
              </button>
            </div>
            {attachments.length === 0 ? (
              <div className="note-form-link-muted">No attachments yet.</div>
            ) : (
              <div className="note-form-attachment-list">
                {attachments.map((file) => (
                  <div key={file.id} className="note-form-attachment">
                    <div className="note-form-attachment-info">
                      <span>{file.fileName || "Untitled file"}</span>
                      <small>{file.fileType || "file"}</small>
                    </div>
                    <div className="note-form-attachment-actions">
                      <button type="button" onClick={() => openAttachment(file)}>Open</button>
                      <button
                        type="button"
                        className="note-form-attachment-link"
                        onClick={() => downloadAttachment(file)}
                      >
                        Download
                      </button>
                      <button
                        type="button"
                        disabled={readOnly}
                        onClick={() => setAttachments((prev) => prev.filter((f) => f.id !== file.id))}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="note-form-attachment-linker">
              <label>Link existing files</label>
              <input
                className="note-form-attachment-search"
                value={fileSearch}
                onChange={(e) => setFileSearch(e.target.value)}
                placeholder="Search files"
                disabled={readOnly}
              />
              {filteredExistingFiles.length === 0 ? (
                <div className="note-form-link-muted">No files found.</div>
              ) : (
                <div className="note-form-attachment-picker">
                  {filteredExistingFiles.map((file) => (
                    <label key={file.id} className="note-form-attachment-option">
                      <input
                        type="checkbox"
                        checked={selectedExistingIds.has(file.id)}
                        onChange={() => toggleExistingFile(file.id)}
                        disabled={readOnly}
                      />
                      <span>{file.fileName || "Untitled file"}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="note-form-card">
            <label>Tags</label>
            <div className="note-form-tag-input">
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    const next = tagInput.split(",").map((t) => t.trim()).filter(Boolean);
                    if (next.length === 0) return;
                    setTags((prev) => {
                      const set = new Set(prev.map((t) => t.toLowerCase()));
                      const merged = [...prev];
                      next.forEach((tag) => {
                        if (!set.has(tag.toLowerCase())) {
                          merged.push(tag.toUpperCase());
                          set.add(tag.toLowerCase());
                        }
                      });
                      return merged;
                    });
                    setTagInput("");
                  }
                }}
                placeholder="Add tag and press Enter"
                disabled={readOnly}
              />
              <button
                type="button"
                onClick={() => {
                  const next = tagInput.split(",").map((t) => t.trim()).filter(Boolean);
                  if (next.length === 0) return;
                  setTags((prev) => {
                    const set = new Set(prev.map((t) => t.toLowerCase()));
                    const merged = [...prev];
                    next.forEach((tag) => {
                      if (!set.has(tag.toLowerCase())) {
                        merged.push(tag.toUpperCase());
                        set.add(tag.toLowerCase());
                      }
                    });
                    return merged;
                  });
                  setTagInput("");
                }}
                disabled={readOnly}
              >
                Add
              </button>
            </div>
            {tags.length === 0 ? (
              <div className="note-form-link-muted">No tags yet.</div>
            ) : (
              <div className="note-form-tag-list">
                {tags.map((tag) => (
                  <span key={tag} className="note-form-tag-chip">
                    {tag}
                    <button
                      type="button"
                      className="note-form-tag-remove"
                      onClick={() => setTags((prev) => prev.filter((t) => t !== tag))}
                      disabled={readOnly}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="note-form-card">
            <label>Links</label>
            <div className="note-form-link-hint">Use [[Note Title]] to link notes.</div>
            {linkMetaLoading ? (
              <div className="note-form-link-muted">Loading links…</div>
            ) : linkedNotes.length === 0 ? (
              <div className="note-form-link-muted">No linked notes yet.</div>
            ) : (
              <div className="note-form-link-list">
                {linkedNotes.map((note) => (
                  <button
                    key={note.id}
                    type="button"
                    className="note-form-link-chip"
                    onClick={() => nav("/notes/new", { state: { note } })}
                  >
                    {note.title || "Untitled"}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="note-form-card">
            <label>Backlinks</label>
            {linkMetaLoading ? (
              <div className="note-form-link-muted">Loading backlinks…</div>
            ) : backlinks.length === 0 ? (
              <div className="note-form-link-muted">No notes link here yet.</div>
            ) : (
              <div className="note-form-link-list">
                {backlinks.map((note) => (
                  <button
                    key={note.id}
                    type="button"
                    className="note-form-link-chip"
                    onClick={() => nav("/notes/new", { state: { note } })}
                  >
                    {note.title || "Untitled"}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="note-form-card">
            <label>Type</label>
            <select value={noteType} onChange={e => setNoteType(e.target.value)} disabled={readOnly}>
              <option value="INFO">Info</option>
              <option value="REMINDER">Reminder</option>
              <option value="WARNING">Warning</option>
            </select>

            <label>Priority</label>
            <div className="note-form-priority-wrap">
              {[1,2,3,4,5].map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPriority(p)}
                  className={`note-form-priority-btn ${priority === p ? "active" : ""}`}
                  disabled={readOnly}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="note-form-card">
            <label>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} disabled={readOnly}>
              <option value="ACTIVE">Active</option>
              <option value="ARCHIVED">Archived</option>
              <option value="DONE">Done</option>
              <option value="DELETED">Deleted</option>
            </select>

            <label>Secure Note</label>
            <div
              onClick={() => {
                if (readOnly) return;
                setSecured((prev) => !prev);
              }}
              className={`note-form-toggle ${secured ? "active" : ""}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (readOnly) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSecured((prev) => !prev);
                }
              }}
              aria-disabled={readOnly}
            >
              <div className={`note-form-toggle-knob ${secured ? "active" : ""}`} />
            </div>

            <label>Protect from Delete</label>
            <div
              onClick={() => {
                if (readOnly) return;
                setDeleteProtected((prev) => !prev);
              }}
              className={`note-form-toggle ${deleteProtected ? "active" : ""}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (readOnly) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setDeleteProtected((prev) => !prev);
                }
              }}
              aria-disabled={readOnly}
            >
              <div className={`note-form-toggle-knob ${deleteProtected ? "active" : ""}`} />
            </div>
          </div>

          <div className="note-form-card">
            <label>Summary</label>
            <textarea
              value={noteSummary}
              onChange={e => setNoteSummary(e.target.value)}
              placeholder="Short summary or key takeaway"
              disabled={readOnly}
            />
          </div>
        </aside>

        <div className="note-form-actions">
          {!readOnly && (
            <button className="note-form-save-btn">{editNote ? "Update" : "Save"}</button>
          )}
          {editNote?.id && (
            <button type="button" className="note-form-email-btn" onClick={handleEmailNote}>
              Email Note
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
